<template>
  <simple-dialog
    ref="dlg"
    :title="dialogTitle"
    width="800"
    confirm-button-text="Send Messages"
    cancel-button-text="Close"
    :close-on-save="false"
    @ok="send"
  >
    <template v-slot:content>
      <v-textarea
        label="Message preview"
        :model-value="preview?.sampleMessage ?? ''"
        rows="6"
        readonly
        class="mb-3"
      />

      <div class="text-body-2 text-medium-emphasis mb-3" v-if="preview">
        {{ preview.eligibleCount }} eligible · {{ preview.sentCount }} sent · {{ preview.notSentCount }} not sent
      </div>

      <v-table density="compact" class="survey-notify-table">
        <thead>
          <tr>
            <th>ITS</th>
            <th>Name</th>
            <th>Mobile</th>
            <th class="text-center" style="width: 80px;">Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="member in preview?.members ?? []" :key="member.itsId">
            <td>{{ member.itsId }}</td>
            <td>{{ member.fullName }}</td>
            <td>{{ member.mobile || "—" }}</td>
            <td class="text-center">
              <v-icon
                :icon="member.messageSent ? 'mdi-check-circle' : 'mdi-close-circle'"
                :color="member.messageSent ? 'success' : 'error'"
                :title="member.outboxStatus || (member.messageSent ? 'Sent' : 'Not sent')"
              />
            </td>
          </tr>
          <tr v-if="!(preview?.members?.length)">
            <td colspan="4" class="text-center pa-4">No eligible members found</td>
          </tr>
        </tbody>
      </v-table>
    </template>
  </simple-dialog>
</template>

<script setup>
import { ref, computed, inject } from "vue";
import { getSurveyNotifyPreview, sendSurveyNotify } from "@/services/surveyService";

const props = defineProps({
  surveyId: { type: Number, default: null },
  surveyTitle: { type: String, default: "" },
});

const loader = inject("loader");
const snackbar = inject("snackBar");
const confirmDialog = inject("confirmDialog");

const dlg = ref(null);
const preview = ref(null);

const dialogTitle = computed(() =>
  props.surveyTitle ? `Send Message — ${props.surveyTitle}` : "Send Message"
);

async function loadPreview() {
  if (!props.surveyId) return;

  loader.show();
  try {
    preview.value = await getSurveyNotifyPreview(props.surveyId);
  } catch (error) {
    snackbar.error(error);
    preview.value = null;
  } finally {
    loader.hide();
  }
}

async function show() {
  await loadPreview();
  if (preview.value)
    dlg.value.show();
}

async function send() {
  if (!props.surveyId || !preview.value?.members?.length) {
    snackbar.error("No eligible members to notify");
    return;
  }

  const confirmed = await confirmDialog.confirm(
    "Send WhatsApp Messages",
    `Send survey notification to eligible members who have not already received a sent message?`,
    "Send",
    "Cancel",
    false
  );

  if (!confirmed)
    return;

  loader.show();
  try {
    const result = await sendSurveyNotify(props.surveyId);
    snackbar.success(
      `Queued ${result.enqueued} message(s). Skipped ${result.skippedSent} already sent, ${result.skippedNoMobile} without mobile.`
    );
    await loadPreview();
  } catch (error) {
    snackbar.error(error);
  } finally {
    loader.hide();
  }
}

defineExpose({ show });
</script>

<style scoped>
.survey-notify-table {
  max-height: 360px;
  overflow-y: auto;
  display: block;
}

.survey-notify-table thead,
.survey-notify-table tbody {
  display: table;
  width: 100%;
  table-layout: fixed;
}
</style>
